import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  Brain,
  Shield,
  Zap,
  Users,
  BarChart3,
  MessageSquare,
  TrendingUp,
  Sparkles,
  Star,
  ChevronRight,
  Menu,
  X,
  Bot,
  User,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import Logo from '../components/Logo';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <Brain size={32} />,
      title: 'Agentic AI',
      description: 'Autonomous AI that proactively identifies and addresses engagement issues before they escalate.',
      color: '#69277F',
    },
    {
      icon: <MessageSquare size={32} />,
      title: 'Seamless Integration',
      description: 'Works where your team already communicates - Slack, Teams, and more. No disruption to workflow.',
      color: '#06BCCA',
    },
    {
      icon: <BarChart3 size={32} />,
      title: 'Real-time Insights',
      description: 'Get instant visibility into engagement patterns with the Dynami Disengagement Index (DDI).',
      color: '#FF68C5',
    },
    {
      icon: <Shield size={32} />,
      title: 'Privacy First',
      description: 'Employee privacy is paramount. Individual responses remain confidential while providing actionable insights.',
      color: '#10B981',
    },
    {
      icon: <Zap size={32} />,
      title: 'Proactive Interventions',
      description: 'Automatically schedule meetings, send manager alerts, and suggest interventions based on engagement signals.',
      color: '#FF9C0D',
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Measurable Impact',
      description: 'Track engagement trends, intervention effectiveness, and ROI with comprehensive analytics.',
      color: '#69277F',
    },
  ];

  const benefits = [
    {
      stat: '40%',
      label: 'Reduction in Turnover',
      description: 'Companies using Dynami see significant reduction in employee churn',
    },
    {
      stat: '3x',
      label: 'Faster Intervention',
      description: 'Identify and address engagement issues 3x faster than traditional methods',
    },
    {
      stat: '85%',
      label: 'Manager Satisfaction',
      description: 'Managers report higher satisfaction with actionable, timely insights',
    },
    {
      stat: '$2.4M',
      label: 'Average Savings',
      description: 'Typical cost savings from reduced turnover and improved productivity',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'VP of People, TechCorp',
      content: 'Dynami has transformed how we approach employee engagement. The proactive interventions have helped us retain top talent and create a more supportive culture.',
      rating: 5,
    },
    {
      name: 'Michael Rodriguez',
      role: 'Engineering Manager, StartupCo',
      content: 'The agentic AI capabilities are incredible. It identifies issues I wouldn\'t have noticed and suggests interventions that actually work. Game changer.',
      rating: 5,
    },
    {
      name: 'Emily Watson',
      role: 'CHRO, GrowthInc',
      content: 'Finally, an engagement platform that respects privacy while delivering real insights. Our employees trust Dynami, and that makes all the difference.',
      rating: 5,
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Connect Your Tools',
      description: 'Integrate with Slack, Teams, Workday, and other tools your team already uses. Setup takes minutes.',
    },
    {
      step: '2',
      title: 'AI Monitors Engagement',
      description: 'Dynami\'s agentic AI continuously analyzes engagement signals through natural conversations and activity patterns.',
    },
    {
      step: '3',
      title: 'Proactive Interventions',
      description: 'When issues are detected, Dynami automatically schedules meetings, alerts managers, and suggests action plans.',
    },
    {
      step: '4',
      title: 'Track Impact',
      description: 'Monitor engagement trends, intervention effectiveness, and ROI through comprehensive dashboards and reports.',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <motion.nav
        style={styles.nav}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div style={styles.navContent}>
          <Logo variant="purple" size="md" />
          <div style={styles.navLinks}>
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#how-it-works" style={styles.navLink}>How It Works</a>
            <a href="#testimonials" style={styles.navLink}>Testimonials</a>
            <button
              style={styles.navButton}
              onClick={() => navigate('/signin')}
            >
              Sign In
            </button>
            <button
              style={{ ...styles.navButton, ...styles.navButtonPrimary }}
              onClick={() => navigate('/signin')}
            >
              Get Started
            </button>
          </div>
          <button
            style={styles.mobileMenuButton}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div style={styles.heroBadge}>
              <Sparkles size={16} />
              <span>Agentic AI for Employee Engagement</span>
            </div>
            <h1 style={styles.heroTitle}>
              Stop Losing Talent.
              <br />
              Start Engaging Proactively.
            </h1>
            <p style={styles.heroDescription}>
              Dynami uses autonomous AI to identify disengagement before it becomes turnover.
              Proactive check-ins, intelligent interventions, and actionable insights—all while
              respecting employee privacy.
            </p>
            <div style={styles.heroActions}>
              <motion.button
                style={styles.ctaButton}
                onClick={() => navigate('/signin')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Free Trial
                <ArrowRight size={20} />
              </motion.button>
              <motion.button
                style={styles.secondaryButton}
                onClick={() => navigate('/signin')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Watch Demo
              </motion.button>
            </div>
            <div style={styles.heroStats}>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>500+</span>
                <span style={styles.heroStatLabel}>Companies</span>
              </div>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>50K+</span>
                <span style={styles.heroStatLabel}>Employees</span>
              </div>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>40%</span>
                <span style={styles.heroStatLabel}>Avg. Turnover Reduction</span>
              </div>
            </div>
          </motion.div>
        </div>
        <motion.div
          style={styles.heroVisual}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Dashboard Mockup */}
          <div style={styles.dashboardMockup}>
            <div style={styles.dashboardHeader}>
              <div style={styles.dashboardHeaderLeft}>
                <div style={styles.dashboardLogo}>D</div>
                <span style={styles.dashboardTitle}>Engagement Dashboard</span>
              </div>
            </div>
            
            {/* Stats Row */}
            <div style={styles.dashboardStats}>
              <div style={styles.dashboardStatCard}>
                <div style={styles.dashboardStatValue}>42%</div>
                <div style={styles.dashboardStatLabel}>Disengaged</div>
              </div>
              <div style={styles.dashboardStatCard}>
                <div style={styles.dashboardStatValue}>28</div>
                <div style={styles.dashboardStatLabel}>High Risk</div>
              </div>
              <div style={styles.dashboardStatCard}>
                <div style={styles.dashboardStatValue}>68</div>
                <div style={styles.dashboardStatLabel}>Avg DDI</div>
              </div>
            </div>

            {/* Employee Table Preview */}
            <div style={styles.dashboardTable}>
              <div style={styles.dashboardTableHeader}>
                <span style={styles.dashboardTableHeaderText}>Employee</span>
                <span style={styles.dashboardTableHeaderText}>Department</span>
                <span style={styles.dashboardTableHeaderText}>DDI</span>
                <span style={styles.dashboardTableHeaderText}>Status</span>
              </div>
              <div style={styles.dashboardTableRow}>
                <div style={styles.dashboardTableEmployee}>
                  <div style={styles.dashboardTableAvatar}>AJ</div>
                  <div>
                    <div style={styles.dashboardTableName}>Alex Johnson</div>
                    <div style={styles.dashboardTableRole}>Software Engineer</div>
                  </div>
                </div>
                <span style={styles.dashboardTableDept}>Engineering</span>
                <div style={styles.dashboardTableDDI}>
                  <div style={styles.dashboardTableBar}>
                    <div style={{ ...styles.dashboardTableBarFill, width: '78%' }} />
                  </div>
                  <span style={styles.dashboardTableDDIValue}>78</span>
                </div>
                <span style={styles.dashboardTableBadgeHigh}>High Risk</span>
              </div>
              <div style={styles.dashboardTableRow}>
                <div style={styles.dashboardTableEmployee}>
                  <div style={styles.dashboardTableAvatar}>SM</div>
                  <div>
                    <div style={styles.dashboardTableName}>Sarah Martinez</div>
                    <div style={styles.dashboardTableRole}>Product Manager</div>
                  </div>
                </div>
                <span style={styles.dashboardTableDept}>Product</span>
                <div style={styles.dashboardTableDDI}>
                  <div style={styles.dashboardTableBar}>
                    <div style={{ ...styles.dashboardTableBarFill, width: '45%', background: '#F59E0B' }} />
                  </div>
                  <span style={styles.dashboardTableDDIValue}>45</span>
                </div>
                <span style={styles.dashboardTableBadgeMedium}>Medium</span>
              </div>
            </div>
          </div>

          {/* Slack Conversation Overlay */}
          <motion.div
            style={styles.slackOverlay}
            initial={{ opacity: 0, x: 50, y: -50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div style={styles.slackHeader}>
              <div style={styles.slackHeaderLeft}>
                <div style={styles.slackIcon}>
                  <MessageSquare size={18} color="white" />
                </div>
                <div>
                  <div style={styles.slackTitle}>#dynami-checkins</div>
                  <div style={styles.slackSubtitle}>Employee View — Alex Johnson</div>
                </div>
              </div>
            </div>
            
            <div style={styles.slackMessages}>
              <div style={styles.slackMessage}>
                <div style={styles.slackMessageAvatar}>
                  <Bot size={18} color="#06BCCA" />
                </div>
                <div style={styles.slackMessageContent}>
                  <div style={styles.slackMessageMeta}>
                    <span style={styles.slackMessageSender}>Dynami</span>
                    <span style={styles.slackMessageTime}>10:32 AM</span>
                  </div>
                  <div style={styles.slackMessageText}>
                    Hey Alex! 👋 Hope you're having a good day. Got a minute for a quick check-in?
                  </div>
                </div>
              </div>

              <div style={{ ...styles.slackMessage, ...styles.slackMessageUser }}>
                <div style={styles.slackMessageAvatar}>
                  <User size={18} color="#69277F" />
                </div>
                <div style={styles.slackMessageContent}>
                  <div style={styles.slackMessageMeta}>
                    <span style={styles.slackMessageSender}>Alex Johnson</span>
                    <span style={styles.slackMessageTime}>10:34 AM</span>
                  </div>
                  <div style={styles.slackMessageText}>
                    Hey, yeah sure. What's up?
                  </div>
                </div>
              </div>

              <div style={styles.slackMessage}>
                <div style={styles.slackMessageAvatar}>
                  <Bot size={18} color="#06BCCA" />
                </div>
                <div style={styles.slackMessageContent}>
                  <div style={styles.slackMessageMeta}>
                    <span style={styles.slackMessageSender}>Dynami</span>
                    <span style={styles.slackMessageTime}>10:34 AM</span>
                  </div>
                  <div style={styles.slackMessageText}>
                    Just wanted to see how things are going! On a scale of 1-5, how would you rate your energy levels at work this week?
                  </div>
                  <div style={styles.slackMessageButtons}>
                    <span style={styles.slackButton}>😫 1</span>
                    <span style={styles.slackButton}>😕 2</span>
                    <span style={styles.slackButton}>😐 3</span>
                    <span style={styles.slackButton}>🙂 4</span>
                    <span style={styles.slackButton}>🤩 5</span>
                  </div>
                </div>
              </div>

              <div style={{ ...styles.slackMessage, ...styles.slackMessageUser }}>
                <div style={styles.slackMessageAvatar}>
                  <User size={18} color="#69277F" />
                </div>
                <div style={styles.slackMessageContent}>
                  <div style={styles.slackMessageMeta}>
                    <span style={styles.slackMessageSender}>Alex Johnson</span>
                    <span style={styles.slackMessageTime}>10:35 AM</span>
                  </div>
                  <div style={styles.slackMessageText}>
                    😕 2 - Been feeling pretty drained lately
                  </div>
                </div>
              </div>

              <div style={styles.slackSystemMessage}>
                <Calendar size={14} />
                <span>1:1 scheduled with Sarah Chen for Thursday 2:00 PM</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" style={styles.section}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Everything You Need to Engage Your Team</h2>
            <p style={styles.sectionDescription}>
              Powerful features designed to help you understand, engage, and retain your workforce
            </p>
          </div>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <motion.div
                key={index}
                style={styles.featureCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(105, 39, 127, 0.15)' }}
              >
                <div
                  style={{
                    ...styles.featureIcon,
                    background: `${feature.color}15`,
                    color: feature.color,
                  }}
                >
                  {feature.icon}
                </div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section style={{ ...styles.section, ...styles.benefitsSection }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Proven Results</h2>
            <p style={styles.sectionDescription}>
              See the measurable impact Dynami delivers for forward-thinking organizations
            </p>
          </div>
          <div style={styles.benefitsGrid}>
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                style={styles.benefitCard}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div style={styles.benefitStat}>{benefit.stat}</div>
                <div style={styles.benefitLabel}>{benefit.label}</div>
                <p style={styles.benefitDescription}>{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={styles.section}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>How Dynami Works</h2>
            <p style={styles.sectionDescription}>
              Get started in minutes and see results in weeks
            </p>
          </div>
          <div style={styles.howItWorksGrid}>
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                style={styles.stepCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div style={styles.stepNumber}>{step.step}</div>
                <h3 style={styles.stepTitle}>{step.title}</h3>
                <p style={styles.stepDescription}>{step.description}</p>
                {index < howItWorks.length - 1 && (
                  <div style={styles.stepArrow}>
                    <ChevronRight size={24} color="#9CA3AF" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" style={{ ...styles.section, ...styles.testimonialsSection }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Trusted by Leading Companies</h2>
            <p style={styles.sectionDescription}>
              See what HR leaders and managers are saying about Dynami
            </p>
          </div>
          <div style={styles.testimonialsGrid}>
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                style={styles.testimonialCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div style={styles.testimonialStars}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#FFC800" color="#FFC800" />
                  ))}
                </div>
                <p style={styles.testimonialContent}>"{testimonial.content}"</p>
                <div style={styles.testimonialAuthor}>
                  <div style={styles.testimonialAvatar}>
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div style={styles.testimonialName}>{testimonial.name}</div>
                    <div style={styles.testimonialRole}>{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <motion.div
          style={styles.ctaContent}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 style={styles.ctaTitle}>Ready to Transform Employee Engagement?</h2>
          <p style={styles.ctaDescription}>
            Join hundreds of companies using Dynami to proactively engage and retain their workforce.
            Start your free trial today—no credit card required.
          </p>
          <div style={styles.ctaActions}>
            <motion.button
              style={styles.ctaButtonLarge}
              onClick={() => navigate('/signin')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Free Trial
              <ArrowRight size={20} />
            </motion.button>
            <motion.button
              style={styles.secondaryButtonLarge}
              onClick={() => navigate('/signin')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Demo
            </motion.button>
          </div>
          <div style={styles.ctaFeatures}>
            <div style={styles.ctaFeature}>
              <Check size={16} color="#10B981" />
              <span>14-day free trial</span>
            </div>
            <div style={styles.ctaFeature}>
              <Check size={16} color="#10B981" />
              <span>No credit card required</span>
            </div>
            <div style={styles.ctaFeature}>
              <Check size={16} color="#10B981" />
              <span>Setup in minutes</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerSection}>
            <Logo variant="purple" size="md" />
            <p style={styles.footerDescription}>
              Agentic AI for proactive employee engagement and retention.
            </p>
          </div>
          <div style={styles.footerLinks}>
            <div style={styles.footerColumn}>
              <h4 style={styles.footerTitle}>Product</h4>
              <a href="#features" style={styles.footerLink}>Features</a>
              <a href="#how-it-works" style={styles.footerLink}>How It Works</a>
              <a href="#pricing" style={styles.footerLink}>Pricing</a>
              <a href="#integrations" style={styles.footerLink}>Integrations</a>
            </div>
            <div style={styles.footerColumn}>
              <h4 style={styles.footerTitle}>Company</h4>
              <a href="#about" style={styles.footerLink}>About</a>
              <a href="#blog" style={styles.footerLink}>Blog</a>
              <a href="#careers" style={styles.footerLink}>Careers</a>
              <a href="#contact" style={styles.footerLink}>Contact</a>
            </div>
            <div style={styles.footerColumn}>
              <h4 style={styles.footerTitle}>Resources</h4>
              <a href="#docs" style={styles.footerLink}>Documentation</a>
              <a href="#support" style={styles.footerLink}>Support</a>
              <a href="#privacy" style={styles.footerLink}>Privacy Policy</a>
              <a href="#terms" style={styles.footerLink}>Terms of Service</a>
            </div>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p style={styles.footerCopyright}>
            © 2024 Dynami. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: '#FFFFFF',
  },
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid #E5E7EB',
    padding: '16px 0',
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  navLink: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#4B5563',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  navButton: {
    padding: '10px 20px',
    background: 'transparent',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#4B5563',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  navButtonPrimary: {
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    border: 'none',
  },
  mobileMenuButton: {
    display: 'none',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#4B5563',
  },
  hero: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '80px 32px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '64px',
    alignItems: 'center',
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    background: 'rgba(105, 39, 127, 0.1)',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#69277F',
    width: 'fit-content',
    marginBottom: '16px',
  },
  heroTitle: {
    fontSize: '64px',
    fontWeight: 700,
    lineHeight: 1.1,
    color: '#1F2937',
    margin: 0,
    fontFamily: 'var(--font-heading)',
  },
  heroDescription: {
    fontSize: '20px',
    lineHeight: 1.6,
    color: '#6B7280',
    margin: 0,
  },
  heroActions: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
  },
  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(105, 39, 127, 0.3)',
  },
  secondaryButton: {
    padding: '16px 32px',
    background: 'white',
    color: '#69277F',
    border: '2px solid #69277F',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  heroStats: {
    display: 'flex',
    gap: '32px',
    marginTop: '32px',
    paddingTop: '32px',
    borderTop: '1px solid #E5E7EB',
  },
  heroStat: {
    display: 'flex',
    flexDirection: 'column',
  },
  heroStatValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  heroStatLabel: {
    fontSize: '14px',
    color: '#6B7280',
  },
  heroVisual: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '500px',
  },
  dashboardMockup: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 20px 60px rgba(105, 39, 127, 0.2)',
    border: '1px solid #E5E7EB',
    width: '100%',
    maxWidth: '600px',
    position: 'relative',
    zIndex: 1,
  },
  dashboardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #E5E7EB',
  },
  dashboardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dashboardLogo: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '18px',
  },
  dashboardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
  },
  dashboardStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  dashboardStatCard: {
    background: '#F9FAFB',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
  },
  dashboardStatValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  dashboardStatLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  dashboardTable: {
    background: '#F9FAFB',
    borderRadius: '12px',
    padding: '16px',
  },
  dashboardTableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: '12px',
    paddingBottom: '12px',
    marginBottom: '12px',
    borderBottom: '1px solid #E5E7EB',
  },
  dashboardTableHeaderText: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  dashboardTableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: '12px',
    padding: '12px 0',
    alignItems: 'center',
  },
  dashboardTableEmployee: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dashboardTableAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '12px',
  },
  dashboardTableName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1F2937',
  },
  dashboardTableRole: {
    fontSize: '11px',
    color: '#6B7280',
  },
  dashboardTableDept: {
    fontSize: '12px',
    color: '#4B5563',
  },
  dashboardTableDDI: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dashboardTableBar: {
    flex: 1,
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  dashboardTableBarFill: {
    height: '100%',
    background: '#EF4444',
    borderRadius: '3px',
  },
  dashboardTableDDIValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#1F2937',
    minWidth: '24px',
  },
  dashboardTableBadgeHigh: {
    padding: '4px 8px',
    background: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    width: 'fit-content',
  },
  dashboardTableBadgeMedium: {
    padding: '4px 8px',
    background: '#FEF3C7',
    color: '#D97706',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    width: 'fit-content',
  },
  slackOverlay: {
    position: 'absolute',
    top: '-40px',
    right: '-60px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid #E5E7EB',
    width: '380px',
    maxHeight: '450px',
    zIndex: 2,
    overflow: 'hidden',
  },
  slackHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid #E5E7EB',
    background: '#FAFAFA',
  },
  slackHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  slackIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: '#06BCCA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  slackTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  slackSubtitle: {
    fontSize: '11px',
    color: '#6B7280',
  },
  slackMessages: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '380px',
    overflowY: 'auto',
  },
  slackMessage: {
    display: 'flex',
    gap: '10px',
  },
  slackMessageUser: {
    flexDirection: 'row-reverse',
  },
  slackMessageAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  slackMessageContent: {
    flex: 1,
  },
  slackMessageMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
  },
  slackMessageSender: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1F2937',
  },
  slackMessageTime: {
    fontSize: '11px',
    color: '#9CA3AF',
  },
  slackMessageText: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: 1.5,
    margin: 0,
  },
  slackMessageButtons: {
    display: 'flex',
    gap: '6px',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  slackButton: {
    padding: '6px 10px',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#374151',
    cursor: 'default',
  },
  slackSystemMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    background: '#FEF3C7',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#D97706',
    fontWeight: 500,
    marginTop: '4px',
  },
  section: {
    padding: '80px 32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '64px',
  },
  sectionTitle: {
    fontSize: '48px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 16px 0',
    fontFamily: 'var(--font-heading)',
  },
  sectionDescription: {
    fontSize: '20px',
    color: '#6B7280',
    margin: 0,
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px',
  },
  featureCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px',
    border: '1px solid #E5E7EB',
    transition: 'all 0.3s ease',
  },
  featureIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  featureTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 12px 0',
  },
  featureDescription: {
    fontSize: '15px',
    color: '#6B7280',
    lineHeight: 1.6,
    margin: 0,
  },
  benefitsSection: {
    background: 'linear-gradient(135deg, #F8F9FC 0%, #E5E7EB 100%)',
    borderRadius: '40px',
    margin: '40px 32px',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  benefitCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  },
  benefitStat: {
    fontSize: '48px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '8px',
  },
  benefitLabel: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '8px',
  },
  benefitDescription: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
  howItWorksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '32px',
    position: 'relative',
  },
  stepCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px',
    border: '1px solid #E5E7EB',
    position: 'relative',
  },
  stepNumber: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '20px',
  },
  stepTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 12px 0',
  },
  stepDescription: {
    fontSize: '15px',
    color: '#6B7280',
    lineHeight: 1.6,
    margin: 0,
  },
  stepArrow: {
    position: 'absolute',
    right: '-16px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  testimonialsSection: {
    background: '#F8F9FC',
  },
  testimonialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px',
  },
  testimonialCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px',
    border: '1px solid #E5E7EB',
  },
  testimonialStars: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
  },
  testimonialContent: {
    fontSize: '16px',
    lineHeight: 1.6,
    color: '#4B5563',
    margin: '0 0 24px 0',
    fontStyle: 'italic',
  },
  testimonialAuthor: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  testimonialAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '16px',
  },
  testimonialName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
  },
  testimonialRole: {
    fontSize: '13px',
    color: '#6B7280',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #431652 0%, #69277F 50%, #06BCCA 100%)',
    padding: '80px 32px',
    textAlign: 'center',
  },
  ctaContent: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  ctaTitle: {
    fontSize: '48px',
    fontWeight: 700,
    color: 'white',
    margin: '0 0 16px 0',
    fontFamily: 'var(--font-heading)',
  },
  ctaDescription: {
    fontSize: '20px',
    color: 'rgba(255, 255, 255, 0.9)',
    margin: '0 0 32px 0',
    lineHeight: 1.6,
  },
  ctaActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  ctaButtonLarge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '18px 40px',
    background: 'white',
    color: '#69277F',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  secondaryButtonLarge: {
    padding: '18px 40px',
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  ctaFeatures: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  ctaFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px',
  },
  footer: {
    background: '#1F2937',
    color: 'white',
    padding: '64px 32px 32px',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '2fr 3fr',
    gap: '64px',
    marginBottom: '48px',
  },
  footerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  footerDescription: {
    fontSize: '15px',
    color: '#9CA3AF',
    margin: 0,
    lineHeight: 1.6,
  },
  footerLinks: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px',
  },
  footerColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  footerTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    margin: '0 0 8px 0',
  },
  footerLink: {
    fontSize: '14px',
    color: '#9CA3AF',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  footerBottom: {
    maxWidth: '1200px',
    margin: '0 auto',
    paddingTop: '32px',
    borderTop: '1px solid #374151',
    textAlign: 'center',
  },
  footerCopyright: {
    fontSize: '14px',
    color: '#9CA3AF',
    margin: 0,
  },
};

export default Landing;

